import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { v4 as uuid } from 'uuid';

import { Button } from '@cognite/cogs-core-v11/components/button/button.tsx';
import { DeleteIcon } from '@cognite/cogs-icons-v11/system-icons/DeleteIcon.tsx';
import { Flex } from '@cognite/cogs-core-v11/components/flex/flex.tsx';
import { Input } from '@cognite/cogs-core-v11/components/input/input.tsx';

import { ComponentContainer } from '../../ui/component/component-container/component-container';
import { useUpsertAppConfigMutation } from '../hooks/use-mutation/use-upsert-app-config-mutation/use-upsert-app-config-mutation';
import { useAppConfigQuery } from '../hooks/use-query/use-app-config-query';
import { SaveConfigurationButton } from '../save-configuration/save-configuration-button';
import type { AppConfig, Discipline } from '../types';

import * as S from './elements';

interface Props {
  hasAppConfigPermission: boolean;
}

export const AppConfigDiscipline: FC<Props> = ({ hasAppConfigPermission }) => {
  const [configuredDisciplines, setConfiguredDisciplines] = useState<
    Discipline[]
  >([]);
  const { data: appConfig, isLoading } = useAppConfigQuery();
  const { mutateAsync: upsertAppConfig } = useUpsertAppConfigMutation();

  useEffect(() => {
    if (appConfig) {
      const appConfigDisciplines =
        appConfig.featureConfiguration?.disciplines ?? [];
      setConfiguredDisciplines(appConfigDisciplines);
    }
  }, [appConfig]);

  const handleSave = async () => {
    if (!appConfig) return;

    const newAppConfig: AppConfig = {
      ...appConfig,
      featureConfiguration: {
        ...appConfig.featureConfiguration,
        disciplines: [...configuredDisciplines],
      },
    };

    await upsertAppConfig({
      newAppConfig,
    });
  };

  const handleOnDisciplineChange = (value: string, externalId: string) => {
    setConfiguredDisciplines((prev) =>
      prev.map((prevDiscipline) => {
        if (prevDiscipline.externalId === externalId) {
          return {
            ...prevDiscipline,
            name: value,
          };
        }
        return prevDiscipline;
      })
    );
  };

  const handleDeleteDiscipline = (externalId: string) => {
    setConfiguredDisciplines((prev) =>
      prev.filter((prevDiscipline) => prevDiscipline.externalId !== externalId)
    );
  };

  return (
    <ComponentContainer>
      <S.Wrapper>
        <Flex direction="column" gap={8}>
          {configuredDisciplines &&
            configuredDisciplines.map((discipline) => (
              <Flex gap={8} key={discipline.externalId}>
                <Input
                  value={discipline.name}
                  onChange={(e) =>
                    handleOnDisciplineChange(
                      e.target.value,
                      discipline.externalId
                    )
                  }
                />
                <Button
                  icon={<DeleteIcon />}
                  type="destructive"
                  onClick={() => handleDeleteDiscipline(discipline.externalId)}
                />
              </Flex>
            ))}
          <S.AddDisciplineButton
            onClick={() =>
              setConfiguredDisciplines([
                ...configuredDisciplines,
                { externalId: uuid(), name: '' },
              ])
            }
          >
            Add discipline
          </S.AddDisciplineButton>
          <SaveConfigurationButton
            onSave={handleSave}
            isDisabled={!hasAppConfigPermission}
            isLoading={isLoading}
          />
        </Flex>
      </S.Wrapper>
    </ComponentContainer>
  );
};
